'use client'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: ${fadeIn} 0.2s ease-out;
    padding: 20px;
`

const ModalContainer = styled.div`
    background: #1e1b2e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    /* Props transitorias con $: sin el $, styled-components reenvía maxWidth
       al div del DOM y React avisa que no reconoce ese atributo.
       (Ojo: nada de backticks en los comentarios de un styled — cierran el
       template literal y rompen el build.) */
    width: ${({ $width }) => $width || '100%'};
    max-width: ${({ $maxWidth }) => $maxWidth || '500px'};
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    animation: ${slideIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
`

const ModalTitle = styled.h2`
    margin: 0;
    padding: 20px 48px 0 24px;
    font-size: 1.15rem;
    font-weight: 700;
    color: #fff;
`

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    z-index: 100;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }
`

const Modal = ({ isOpen, onClose, title, children, ...props }) => {
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
        return () => setMounted(false)
    }, [])

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    if (!isOpen || !mounted) return null

    return createPortal(
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()} $width={props.width} $maxWidth={props.maxWidth}>
                {title ? <ModalTitle>{title}</ModalTitle> : null}
                {children}
                {!props.hideCloseButton && (
                    <CloseButton onClick={onClose}>
                        <CloseIcon style={{ fontSize: 20 }} />
                    </CloseButton>
                )}
            </ModalContainer>
        </ModalOverlay>,
        document.body
    )
}

export default Modal
